import { StyleSheet, View } from 'react-native'
import Animated, { Easing, FadeOutUp, Keyframe } from 'react-native-reanimated'

import { ApplePayIcon } from '../icons/PaymentBrandIcons'
import {
  useBackdropBlur,
  useLiquidGlass,
} from '../providers/NativeCapabilitiesContext'
import { isDarkColor } from '../theme/isDarkColor'
import { Box, Text, useTheme } from '../theme/restyle'
import { withAlpha } from '../utils/withAlpha'

// The failure banner's glyph slot, so both cards start their copy at 64.
const GLYPH_SLOT = 26
const GLYPH_HEIGHT = 21

const CARD_RADIUS = 20
// Thinner than the deposit sheet's glass: a card, not a full screen.
const SURFACE_OPACITY = 0.5
const GLASS_DARK_MATERIAL = '#2A2A2A'
// Enter from above, the way a notification arrives; exit is the lift the
// copied-address pill already uses, so both toasts leave the same way.
const enterAnimation = new Keyframe({
  0: { opacity: 0, transform: [{ translateY: -12 }] },
  100: {
    opacity: 1,
    transform: [{ translateY: 0 }],
    easing: Easing.bezier(0.34, 1.36, 0.64, 1),
  },
}).duration(450)

const exitAnimation = FadeOutUp.duration(350)

export interface DepositReceivedToastProps {
  /** The amount that landed, e.g. "$99.50 deposit received". */
  title: string
  /** Where it came from, e.g. "from ApplePay". */
  detail: string
}

/**
 * The deposit confirmation that replaces the payment sheet.
 *
 * - Owns no timer and no positioning: whatever mounts it decides both.
 * - Outlives the sheet — the flow closes on a cleared charge, and this is what
 *   says the money arrived.
 * - Glass like GlassSheetBackground: glass owns the tint, so the fill drops
 *   out under it rather than flattening the refraction.
 */
export function DepositReceivedToast({
  title,
  detail,
}: DepositReceivedToastProps) {
  const theme = useTheme()
  const accent = theme.content.elements.primary
  const material = useBackdropBlur()
  const glass = useLiquidGlass()
  const fill = withAlpha(
    isDarkColor(theme.background.modal)
      ? GLASS_DARK_MATERIAL
      : theme.background.modal,
    SURFACE_OPACITY,
  )
  return (
    <Animated.View entering={enterAnimation} exiting={exitAnimation}>
      <Box
        flexDirection="row"
        accessibilityRole="alert"
        accessibilityLabel={`${title}, ${detail}`}
        style={styles.card}
      >
        {/* Under the fill, so the material samples the host app. Keeps Glass's
            specular rim, which the sheet cuts away and the card draws. */}
        <View style={[StyleSheet.absoluteFill, styles.clip]}>{material()}</View>
        <View
          pointerEvents="none"
          style={[
            StyleSheet.absoluteFill,
            styles.wash,
            { backgroundColor: glass ? 'transparent' : fill },
          ]}
        />
        <Box style={styles.glyph}>
          <ApplePayIcon size={GLYPH_HEIGHT} color={accent} />
        </Box>
        <Box flex={1} style={styles.copy}>
          <Text weight="medium" style={[styles.text, { color: accent }]}>
            {title}
          </Text>
          <Text style={[styles.text, styles.detail, { color: accent }]}>
            {detail}
          </Text>
        </Box>
      </Box>
    </Animated.View>
  )
}

const styles = StyleSheet.create({
  // The failure banner's measurements at this card's height.
  card: {
    minHeight: 72,
    borderRadius: CARD_RADIUS,
    paddingVertical: 18,
    paddingLeft: 21,
    paddingRight: 10,
    gap: 17,
    shadowColor: '#000',
    shadowOpacity: 0.25,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 4 },
    elevation: 4,
  },
  clip: { borderRadius: CARD_RADIUS, overflow: 'hidden' },
  wash: { borderRadius: CARD_RADIUS },
  glyph: { width: GLYPH_SLOT, alignItems: 'center', justifyContent: 'center' },
  copy: { justifyContent: 'center' },
  text: { fontSize: 15, lineHeight: 18, letterSpacing: -0.225 },
  detail: { opacity: 0.5 },
})
